import { useState } from 'react'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import type { AuthSession, PaymentSettingsInput } from '../../../../types/plans'
import { PaymentSettingsField } from '../../../payments/PaymentSettingsField'
import { Button } from '@unified-lk/ui'
import {
  workspaceActionsClassName,
  workspaceFieldClassName,
  workspaceFieldLabelClassName,
  workspaceInputClassName,
  workspaceMetricClassName,
} from '../../../workspaceStyles'
import { formatDate, formatShortDateTimeValue, toSqlDateTimeValue } from '../../../../lib/format'

// ─── Value type shared by constructor SharePanel and MIS MisSharePanel ────────

export interface PublicShareFormValue {
  is_active: boolean
  expires_at: string | null
  hide_prices: boolean
  /** Plaintext password staff enters; backend stores only the hash. Null = no protection. */
  access_password: string | null
  custom_title?: string
  note?: string
  discount_percent?: number | null
  offer_deadline?: string | null
  payment?: PaymentSettingsInput | null
}

export interface ShareAnalytics {
  view_count: number
  last_viewed_at: string | null
  last_scroll_depth_percent: number | null
  last_read_time_seconds: number | null
}

function formatSeconds(s: number | null | undefined): string {
  if (!s || s <= 0) return '—'
  if (s < 60) return `${Math.round(s)} сек`
  return `${Math.floor(s / 60)} мин ${Math.round(s % 60)} сек`
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface PublicShareFormProps {
  value: PublicShareFormValue
  onChange: (patch: Partial<PublicShareFormValue>) => void

  /** Show custom_title, note, discount_percent, offer_deadline, is_active toggle.
   *  MIS plans = true; constructor (uses separate panels) = false. */
  showExtendedFields?: boolean

  publicUrl?: string | null
  /** True when backend has a password hash stored (returned as has_password from API). */
  hasPassword?: boolean
  analytics?: ShareAnalytics | null

  onActivate?: () => void
  onDeactivate?: () => void
  saving?: boolean
  error?: string | null
  message?: string | null
  autoSaveStatus?: 'idle' | 'pending' | 'saved' | 'error'
  /** Immediate save when expiry field loses focus */
  onExpiresAtCommit?: () => void

  bootstrap: TenantBootstrapModel
  session: AuthSession
}

// ─── Component ────────────────────────────────────────────────────────────────

export function PublicShareForm({
  value,
  onChange,
  showExtendedFields = false,
  publicUrl,
  hasPassword,
  analytics,
  onActivate,
  onDeactivate,
  saving = false,
  error,
  message,
  autoSaveStatus,
  onExpiresAtCommit,
  bootstrap,
  session,
}: PublicShareFormProps) {
  const [copied, setCopied] = useState(false)
  const [showPasswordInput, setShowPasswordInput] = useState(
    Boolean(value.access_password) || Boolean(hasPassword),
  )

  function handleCopy() {
    if (!publicUrl) return
    void navigator.clipboard.writeText(publicUrl).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  function handlePasswordToggle(checked: boolean) {
    setShowPasswordInput(checked)
    if (!checked) onChange({ access_password: null })
  }

  return (
    <div className="grid gap-4">
      {/* ── Extended fields: МИС-specific ── */}
      {showExtendedFields && (
        <>
          <label className={workspaceFieldClassName}>
            <span className={workspaceFieldLabelClassName}>Заголовок (переопределить название плана)</span>
            <input
              className={workspaceInputClassName}
              type="text"
              value={value.custom_title ?? ''}
              onChange={(e) => onChange({ custom_title: e.target.value })}
              placeholder="Например: Коронка на зуб 22 — спецпредложение"
            />
          </label>

          <label className={workspaceFieldClassName}>
            <span className={workspaceFieldLabelClassName}>Комментарий к плану</span>
            <textarea
              className={`${workspaceInputClassName} min-h-[80px] resize-y`}
              value={value.note ?? ''}
              onChange={(e) => onChange({ note: e.target.value })}
              placeholder="Пояснение для пациента, условия, рекомендации..."
            />
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className={workspaceFieldClassName}>
              <span className={workspaceFieldLabelClassName}>Скидка, % (за наличные / общая)</span>
              <input
                className={workspaceInputClassName}
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={value.discount_percent ?? ''}
                onChange={(e) => onChange({
                  discount_percent: e.target.value ? Number(e.target.value) : null,
                })}
                placeholder="Например: 10"
              />
            </label>
            <label className={workspaceFieldClassName}>
              <span className={workspaceFieldLabelClassName}>Предложение действует до</span>
              <input
                className={workspaceInputClassName}
                type="date"
                value={value.offer_deadline ? value.offer_deadline.slice(0, 10) : ''}
                onChange={(e) => onChange({
                  offer_deadline: e.target.value || null,
                })}
              />
            </label>
          </div>
        </>
      )}

      {/* ── Core fields ── */}
      <label className={workspaceFieldClassName}>
        <span className={workspaceFieldLabelClassName}>Ссылка истекает</span>
        <input
          className={workspaceInputClassName}
          type="datetime-local"
          value={formatShortDateTimeValue(value.expires_at)}
          onChange={(e) => onChange({
            expires_at: e.target.value ? toSqlDateTimeValue(e.target.value) : null,
          })}
          onBlur={() => onExpiresAtCommit?.()}
        />
      </label>

      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300 accent-sky-500"
            checked={value.hide_prices}
            onChange={(e) => onChange({ hide_prices: e.target.checked })}
          />
          Скрыть цены
        </label>

        {/* Show active toggle only when link is already active (to toggle it off).
            When inactive, activation happens exclusively via the button below. */}
        {showExtendedFields && value.is_active && (
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 accent-sky-500"
              checked={value.is_active}
              onChange={(e) => onChange({ is_active: e.target.checked })}
            />
            Ссылка активна
          </label>
        )}
      </div>

      {/* ── Password protection ── */}
      <div className="rounded-[18px] border border-slate-200/70 bg-white px-4 py-3 space-y-3">
        <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer select-none">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300 accent-sky-500"
            checked={showPasswordInput}
            onChange={(e) => handlePasswordToggle(e.target.checked)}
          />
          <svg className="h-4 w-4 text-slate-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          Открытие по паролю
        </label>

        {showPasswordInput && (
          <div className="space-y-1.5">
            <p className="text-[11px] text-slate-400">
              Пациент увидит форму ввода пароля и получит доступ к плану только после его ввода.
              {hasPassword && !value.access_password && (
                <span className="ml-1 text-emerald-600 font-medium">Пароль уже установлен — оставьте поле пустым, чтобы не менять.</span>
              )}
            </p>
            <input
              className={workspaceInputClassName}
              type="text"
              autoComplete="new-password"
              value={value.access_password ?? ''}
              onChange={(e) => onChange({ access_password: e.target.value || null })}
              placeholder={hasPassword ? '••••••  (не менять)' : 'Придумайте пароль для пациента'}
            />
          </div>
        )}
      </div>

      {/* ── Payment settings ── */}
      <div className="rounded-[18px] border border-slate-200/70 bg-white px-4 py-3">
        <PaymentSettingsField
          value={value.payment ?? null}
          onChange={(payment) => onChange({ payment })}
          discountPercent={value.discount_percent ?? null}
        />
      </div>

      {/* ── Public URL ── */}
      {publicUrl && (
        <div className="rounded-[18px] border border-slate-200/70 bg-white px-4 py-3">
          <span className="block text-sm font-semibold text-slate-700 mb-1">Публичный URL</span>
          <div className="flex items-center gap-2">
            <strong className="flex-1 break-all text-sm text-slate-900 font-mono">{publicUrl}</strong>
            <button
              type="button"
              className="shrink-0 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200"
              onClick={handleCopy}
            >
              {copied ? 'Скопировано!' : 'Копировать'}
            </button>
          </div>
        </div>
      )}

      {/* ── Analytics ── */}
      {analytics && value.is_active && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className={workspaceMetricClassName}>
            <span className="text-[13px] text-slate-500">Просмотры</span>
            <strong className="text-slate-900">{analytics.view_count}</strong>
          </div>
          <div className={workspaceMetricClassName}>
            <span className="text-[13px] text-slate-500">Последний</span>
            <strong className="text-sm text-slate-900">{formatDate(analytics.last_viewed_at)}</strong>
          </div>
          <div className={workspaceMetricClassName}>
            <span className="text-[13px] text-slate-500">Скролл</span>
            <strong className="text-slate-900">
              {analytics.last_scroll_depth_percent !== null ? `${analytics.last_scroll_depth_percent}%` : '—'}
            </strong>
          </div>
          <div className={workspaceMetricClassName}>
            <span className="text-[13px] text-slate-500">Время чтения</span>
            <strong className="text-slate-900">{formatSeconds(analytics.last_read_time_seconds)}</strong>
          </div>
        </div>
      )}

      {/* ── Actions ── */}
      <div className={workspaceActionsClassName}>
        {!value.is_active && onActivate && (
          <Button type="button" onClick={onActivate} disabled={saving}>
            {saving ? 'Сохраняем...' : 'Активировать ссылку'}
          </Button>
        )}
        {value.is_active && (
          <>
            {publicUrl && (
              <Button variant="secondary" type="button" onClick={handleCopy}>
                {copied ? 'Скопировано' : 'Копировать ссылку'}
              </Button>
            )}
            {publicUrl && (
              <Button
                variant="secondary"
                type="button"
                onClick={() => window.open(publicUrl, '_blank', 'noopener,noreferrer')}
              >
                Открыть
              </Button>
            )}
            {onDeactivate && (
              <Button variant="secondary" type="button" onClick={onDeactivate} disabled={saving}>
                Деактивировать
              </Button>
            )}
          </>
        )}

        {autoSaveStatus && autoSaveStatus !== 'idle' && (
          <span className={`ml-auto text-xs ${
            autoSaveStatus === 'pending' ? 'text-slate-400' :
            autoSaveStatus === 'saved'   ? 'text-emerald-600' :
            'text-red-500'
          }`}>
            {autoSaveStatus === 'pending' && '● сохраняется...'}
            {autoSaveStatus === 'saved'   && '✓ сохранено'}
            {autoSaveStatus === 'error'   && '✕ ошибка сохранения'}
          </span>
        )}
      </div>

      {error   && <p className="rounded-[18px] bg-red-50  px-4 py-3 text-sm text-red-700">{error}</p>}
      {message && <p className="rounded-[18px] bg-sky-50  px-4 py-3 text-sm leading-6 text-sky-800">{message}</p>}
    </div>
  )
}
